import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, getQueryKey } from '@feature/utils';

import * as api from './ResearchCenterApi';
import {
  CenterReport,
  CenterReportCriteriaData,
  CenterReportCriteriaDataRequest,
  CenterReportCriteriaInfra,
  CenterReportCriteriaInfraRequest,
  CenterReportTemplate,
  ResearchCenter,
  ResearchCenterRequest,
} from './ResearchCenterTypes';

const researchCenterKey = getQueryKey('researchCenter');

export function getResearchCentersOptions(): UseSuspenseQueryOptions<ResearchCenter[]> {
  return {
    queryKey: researchCenterKey.list(),
    queryFn: () => api.getAll(),
  };
}

export function getResearchCenterOptions(researchCenterId: Id): UseSuspenseQueryOptions<ResearchCenter> {
  return {
    queryKey: researchCenterKey.one(researchCenterId),
    queryFn: () => api.get(researchCenterId),
  };
}

export function useResearchCenterEdit(researchCenterId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ResearchCenterRequest) => api.edit(researchCenterId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.root(),
      }),
  });
}

export function useResearchCenterCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ResearchCenterRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.root(),
      }),
  });
}

export function useResearchCenterDelete(researchCenterId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(researchCenterId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.root(),
      }),
  });
}

export function getCenterLastReportOptions(): UseSuspenseQueryOptions<CenterReport> {
  return {
    queryKey: researchCenterKey.as(['report', 'last']),
    queryFn: () => api.getLastReport(),
  };
}

export function getCenterLastReportTemplateOptions(): UseSuspenseQueryOptions<CenterReportTemplate> {
  return {
    queryKey: researchCenterKey.as(['report', 'template']),
    queryFn: () => api.getReportTemplate(),
  };
}

export function getCenterReportCriteriaInfraOptions(labId: Id): UseSuspenseQueryOptions<CenterReportCriteriaInfra[]> {
  return {
    queryKey: researchCenterKey.as(['criteria', 'infra', labId]),
    queryFn: () => api.getReportCriteriaInfra(labId),
  };
}

export function useAddCenterReportCriteriaInfra(labId: Id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CenterReportCriteriaInfraRequest) => api.addReportCriteriaInfra(labId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.as(['criteria', 'infra', labId]),
      }),
  });
}

export function useRemoveCenterReportCriteriaInfra(labId: Id, infraId: Id) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.removeReportCriteriaInfra(labId, infraId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.as(['criteria', 'infra', labId]),
      }),
  });
}

export function getCenterReportCriteriaDataOptions(
  labId: Id,
  criteriaCode: string,
): UseSuspenseQueryOptions<CenterReportCriteriaData> {
  return {
    queryKey: researchCenterKey.as(['criteria', 'data', labId, criteriaCode]),
    queryFn: () => api.getReportCriteriaData(labId, criteriaCode),
  };
}

export function useSaveCenterReportCriteriaData(labId: Id, criteriaCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CenterReportCriteriaDataRequest) => api.saveReportCriteriaData(labId, criteriaCode, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.as(['criteria', 'data', labId, criteriaCode]),
      }),
  });
}

export function useSaveCenterReportCriteriaComplete(labId: Id, criteriaCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isComplete: boolean) => api.saveReportCriteriaCompleted(labId, criteriaCode, isComplete),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchCenterKey.as(['criteria', 'data', labId, criteriaCode]),
      }),
  });
}
